import React, { useEffect, useState, useRef } from "react";
import api, { formatApiError } from "@/lib/api";
import { formatDate } from "@/lib/format";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import {
  Plus,
  Upload,
  Search,
  Building2,
  Trash2,
  FileSpreadsheet,
  ArrowLeft,
  Wallet,
  TrendingUp,
  TrendingDown,
  CreditCard,
  Calendar,
  Filter,
  ChevronLeft,
  ChevronRight,
  Receipt,
  IndianRupee,
  Phone,
  Mail,
  FileText,
  Clock,
  Pencil,
  MapPin,
  StickyNote,
} from "lucide-react";

export default function Suppliers() {
  const [items, setItems] = useState([]);
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [editSupplier, setEditSupplier] = useState(null);
  const [form, setForm] = useState({ name: "", phone: "", email: "", gst: "", address: "", notes: "" });
  const [editForm, setEditForm] = useState({ name: "", phone: "", email: "", gst: "", address: "", notes: "" });
  const [saving, setSaving] = useState(false);
  const [selected, setSelected] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [toDelete, setToDelete] = useState(null);
  const fileRef = useRef();

  const fetch = async (query = "") => {
    try {
      const { data } = await api.get("/suppliers", { params: { q: query, include_balance: true } });
      setItems(data.items);
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  useEffect(() => {
    fetch("");
  }, []);

  useEffect(() => {
    const t = setTimeout(() => fetch(q), 250);
    return () => clearTimeout(t);
  }, [q]);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) return toast.error("Supplier name is required");
    setSaving(true);
    try {
      const { data: newSupplier } = await api.post("/suppliers", form);
      toast.success("Supplier added");
      setOpen(false);
      setForm({ name: "", phone: "", email: "", gst: "", address: "", notes: "" });
      fetch(q);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };

  const onUpload = async (file) => {
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    setUploading(true);
    try {
      const { data } = await api.post("/suppliers/upload", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      let msg = `Added ${data.inserted}, updated ${data.updated}.`;
      if (data.opening_balances_set > 0) {
        msg += ` Set ${data.opening_balances_set} opening balance(s).`;
      }
      toast.success(msg);
      fetch(q);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const remove = async () => {
    if (!toDelete) return;
    try {
      await api.delete(`/suppliers/${toDelete.id}`);
      toast.success("Deleted");
      setToDelete(null);
      fetch(q);
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const openEditDialog = (supplier, e) => {
    e.stopPropagation();
    setEditSupplier(supplier);
    setEditForm({
      name: supplier.name || "",
      phone: supplier.phone || "",
      email: supplier.email || "",
      gst: supplier.gst || "",
      address: supplier.address || "",
      notes: supplier.notes || "",
    });
    setEditOpen(true);
  };

  const submitEdit = async (e) => {
    e.preventDefault();
    if (!editForm.name.trim()) return toast.error("Supplier name is required");
    setSaving(true);
    try {
      await api.put(`/suppliers/${editSupplier.id}`, editForm);
      toast.success("Supplier updated");
      setEditOpen(false);
      setEditSupplier(null);
      fetch(q);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };

  if (selected) {
    return <SupplierDetail supplier={selected} onBack={() => { setSelected(null); fetch(q); }} />;
  }

  // Calculate totals
  const totalPayable = items.reduce((sum, s) => sum + (s.balance > 0 ? s.balance : 0), 0);
  const totalAdvance = items.reduce((sum, s) => sum + (s.balance < 0 ? Math.abs(s.balance) : 0), 0);

  return (
    <div className="fade-up" data-testid="suppliers-page">
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4 mb-6">
        <div>
          <p className="text-xs uppercase tracking-widest text-muted-foreground mb-2">Vendors</p>
          <h1 className="font-heading text-3xl tracking-tight">Suppliers</h1>
          <p className="text-sm text-muted-foreground mt-1">Manage vendor contacts, balances, and ledger.</p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <input
            type="file"
            ref={fileRef}
            accept=".xls,.xlsx"
            className="hidden"
            onChange={(e) => onUpload(e.target.files?.[0])}
            data-testid="supplier-file-input"
          />
          <Button variant="outline" onClick={() => fileRef.current?.click()} disabled={uploading} data-testid="upload-suppliers-btn">
            <Upload className="w-4 h-4 mr-2" /> {uploading ? "Uploading…" : "Upload Excel"}
          </Button>
          <Button onClick={() => setOpen(true)} style={{ backgroundColor: "#9E6D5B" }} data-testid="add-supplier-btn">
            <Plus className="w-4 h-4 mr-2" /> Add supplier
          </Button>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-4">
        <Card className="p-3">
          <div className="text-xs uppercase tracking-wider text-muted-foreground mb-1">Total Suppliers</div>
          <div className="font-mono-data text-2xl font-semibold">{items.length}</div>
        </Card>
        <Card className="p-3">
          <div className="text-xs uppercase tracking-wider text-muted-foreground mb-1">Total Payable</div>
          <div className="font-mono-data text-2xl font-semibold text-red-600">₹{totalPayable.toLocaleString()}</div>
        </Card>
        <Card className="p-3">
          <div className="text-xs uppercase tracking-wider text-muted-foreground mb-1">Total Advance</div>
          <div className="font-mono-data text-2xl font-semibold text-green-600">₹{totalAdvance.toLocaleString()}</div>
        </Card>
        <Card className="p-3">
          <div className="text-xs uppercase tracking-wider text-muted-foreground mb-1">Net Position</div>
          <div className={`font-mono-data text-2xl font-semibold ${totalPayable - totalAdvance > 0 ? "text-red-600" : "text-green-600"}`}>
            ₹{Math.abs(totalPayable - totalAdvance).toLocaleString()}
            <span className="text-xs ml-1">{totalPayable - totalAdvance > 0 ? "(owe)" : "(adv)"}</span>
          </div>
        </Card>
      </div>

      <Card className="p-3 mb-4 text-xs text-muted-foreground">
        <div className="flex items-center gap-2">
          <FileSpreadsheet className="w-4 h-4" />
          Excel columns: <b>Name</b>, Phone, Email, GST, Address, Notes, <b className="text-amber-600">Opening Balance</b> (new)
        </div>
      </Card>

      <div className="relative mb-4">
        <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
        <Input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Search suppliers by name, phone, email or GST…"
          className="pl-9 h-11 bg-white"
          data-testid="supplier-search-input"
        />
      </div>

      <Card className="overflow-hidden" data-testid="suppliers-table-card">
        {/* Sticky Header */}
        <div className="bg-[hsl(var(--muted))] px-4 py-2.5 border-b flex items-center justify-between sticky top-0 z-10">
          <div className="text-xs uppercase tracking-wider text-muted-foreground font-medium">
            Supplier Details
          </div>
          <div className="flex items-center gap-6 text-xs uppercase tracking-wider text-muted-foreground font-medium">
            <span className="w-24 text-right">Balance</span>
            <span className="w-16 text-center">Actions</span>
          </div>
        </div>
        
        {/* Supplier List */}
        <div className="divide-y divide-[hsl(var(--border))]">
          {items.map((s) => {
            const balance = s.balance || 0;
            const isPayable = balance > 0;
            const isAdvance = balance < 0;
            return (
              <div
                key={s.id}
                className="px-4 py-3 hover:bg-muted/30 transition-colors"
                data-testid={`supplier-row-${s.id}`}
              >
                {/* Top Row: Name + Balance + Actions */}
                <div className="flex items-start justify-between gap-4">
                  {/* Left: Supplier Name (clickable) */}
                  <div 
                    className="flex-1 min-w-0 cursor-pointer group"
                    onClick={() => setSelected(s)}
                    data-testid={`supplier-open-${s.id}`}
                  >
                    <div className="flex items-center gap-2">
                      <Building2 className="w-4 h-4 text-[#9E6D5B] flex-shrink-0" />
                      <span className="font-semibold text-sm group-hover:underline underline-offset-4 break-words">
                        {s.name}
                      </span>
                    </div>
                  </div>
                  
                  {/* Right: Balance + Actions */}
                  <div className="flex items-center gap-3 flex-shrink-0">
                    {/* Balance */}
                    <div className="w-24 text-right">
                      {balance !== 0 ? (
                        <span
                          className={`font-mono-data text-sm font-bold ${
                            isPayable ? "text-red-600" : "text-green-600"
                          }`}
                          data-testid={`supplier-balance-${s.id}`}
                          title={isPayable ? "Payable" : "Advance"}
                        >
                          {isPayable ? "−" : "+"}₹{Math.abs(balance).toLocaleString()}
                        </span>
                      ) : (
                        <span className="text-muted-foreground text-xs">—</span>
                      )}
                    </div>
                    
                    {/* Actions */}
                    <div className="flex items-center gap-0.5 w-16 justify-center">
                      <Button 
                        size="sm" 
                        variant="ghost" 
                        className="h-7 w-7 p-0"
                        onClick={(e) => openEditDialog(s, e)} 
                        data-testid={`edit-supplier-btn-${s.id}`}
                        title="Edit supplier"
                      >
                        <Pencil className="w-3.5 h-3.5 text-muted-foreground" />
                      </Button>
                      <Button 
                        size="sm" 
                        variant="ghost"
                        className="h-7 w-7 p-0"
                        onClick={(e) => { e.stopPropagation(); setToDelete(s); }} 
                        data-testid={`delete-supplier-btn-${s.id}`}
                        title="Delete supplier"
                      >
                        <Trash2 className="w-3.5 h-3.5 text-destructive" />
                      </Button>
                    </div>
                  </div>
                </div>
                
                {/* Bottom Row: Compact details grid */}
                <div className="mt-2 flex flex-wrap items-center gap-x-4 gap-y-1.5 text-xs text-muted-foreground pl-6">
                  {/* Phone */}
                  {s.phone && (
                    <div className="flex items-center gap-1" title="Phone">
                      <Phone className="w-3 h-3" />
                      <span className="font-mono-data">{s.phone}</span>
                    </div>
                  )}
                  
                  {/* Email */}
                  {s.email && (
                    <div className="flex items-center gap-1 max-w-[180px]" title={s.email}>
                      <Mail className="w-3 h-3 flex-shrink-0" />
                      <span className="truncate">{s.email}</span>
                    </div>
                  )}
                  
                  {/* GST */}
                  {s.gst && (
                    <div className="flex items-center gap-1" title="GST Number">
                      <FileText className="w-3 h-3" />
                      <span className="font-mono-data text-[11px]">{s.gst}</span>
                    </div>
                  )}
                  
                  {/* Address */}
                  {s.address && (
                    <div className="flex items-center gap-1 max-w-[200px]" title={s.address}>
                      <MapPin className="w-3 h-3 flex-shrink-0" />
                      <span className="truncate">{s.address}</span>
                    </div>
                  )}
                  
                  {/* Notes */}
                  {s.notes && (
                    <div className="flex items-center gap-1 max-w-[150px]" title={s.notes}>
                      <StickyNote className="w-3 h-3 flex-shrink-0" />
                      <span className="truncate italic">{s.notes}</span>
                    </div>
                  )}
                  
                  {/* Show dash if no details */}
                  {!s.phone && !s.email && !s.gst && !s.address && !s.notes && (
                    <span className="text-muted-foreground/50">No contact details</span>
                  )}
                </div>
              </div>
            );
          })}
          
          {/* Empty State */}
          {!items.length && (
            <div className="p-10 text-center text-sm text-muted-foreground">
              No suppliers yet. Upload an Excel or add one manually.
            </div>
          )}
        </div>
      </Card>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="add-supplier-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading tracking-tight">Add supplier</DialogTitle>
            <DialogDescription>Capture a new vendor you restock from.</DialogDescription>
          </DialogHeader>
          <form onSubmit={submit} className="space-y-3">
            {[
              { k: "name", label: "Name *", required: true },
              { k: "phone", label: "Phone" },
              { k: "email", label: "Email" },
              { k: "gst", label: "GST" },
              { k: "address", label: "Address" },
              { k: "notes", label: "Notes" },
            ].map((f) => (
              <div key={f.k}>
                <Label className="text-xs uppercase tracking-wider text-muted-foreground">{f.label}</Label>
                <Input
                  value={form[f.k]}
                  onChange={(e) => setForm({ ...form, [f.k]: e.target.value })}
                  required={!!f.required}
                  className="mt-1"
                  data-testid={`supplier-${f.k}-input`}
                />
              </div>
            ))}
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setOpen(false)} data-testid="supplier-cancel-btn">
                Cancel
              </Button>
              <Button type="submit" disabled={saving} style={{ backgroundColor: "#9E6D5B" }} data-testid="supplier-submit-btn">
                {saving ? "Saving…" : "Add supplier"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Edit Supplier Dialog */}
      <Dialog open={editOpen} onOpenChange={(o) => { setEditOpen(o); if (!o) setEditSupplier(null); }}>
        <DialogContent data-testid="edit-supplier-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading tracking-tight">Edit supplier</DialogTitle>
            <DialogDescription>Update supplier details.</DialogDescription>
          </DialogHeader>
          <form onSubmit={submitEdit} className="space-y-3">
            {[
              { k: "name", label: "Name *", required: true },
              { k: "phone", label: "Phone" },
              { k: "email", label: "Email" },
              { k: "gst", label: "GST" },
              { k: "address", label: "Address" },
              { k: "notes", label: "Notes" },
            ].map((f) => (
              <div key={f.k}>
                <Label className="text-xs uppercase tracking-wider text-muted-foreground">{f.label}</Label>
                <Input
                  value={editForm[f.k]}
                  onChange={(e) => setEditForm({ ...editForm, [f.k]: e.target.value })}
                  required={!!f.required}
                  className="mt-1"
                  data-testid={`edit-supplier-${f.k}-input`}
                />
              </div>
            ))}
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => { setEditOpen(false); setEditSupplier(null); }} data-testid="edit-supplier-cancel-btn">
                Cancel
              </Button>
              <Button type="submit" disabled={saving} style={{ backgroundColor: "#9E6D5B" }} data-testid="edit-supplier-submit-btn">
                {saving ? "Saving…" : "Update supplier"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!toDelete} onOpenChange={(o) => !o && setToDelete(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete {toDelete?.name}?</AlertDialogTitle>
            <AlertDialogDescription>
              Existing purchase records will keep the supplier name but new orders will no longer link to this vendor.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={remove} data-testid="supplier-delete-confirm">Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

function SupplierDetail({ supplier, onBack }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("overview");
  const [ledgerData, setLedgerData] = useState(null);
  const [ledgerLoading, setLedgerLoading] = useState(false);
  const [ledgerPage, setLedgerPage] = useState(1);
  const [ledgerFilter, setLedgerFilter] = useState("all");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [paymentOpen, setPaymentOpen] = useState(false);
  const [openingBalanceOpen, setOpeningBalanceOpen] = useState(false);

  useEffect(() => {
    setLoading(true);
    Promise.all([
      api.get(`/suppliers/${supplier.id}/products`),
      api.get(`/suppliers/${supplier.id}`),
    ])
      .then(([prodRes, supplierRes]) => {
        setData({
          ...prodRes.data,
          supplierDetail: supplierRes.data,
        });
      })
      .catch((e) => toast.error(formatApiError(e)))
      .finally(() => setLoading(false));
  }, [supplier.id]);

  const fetchLedger = async () => {
    setLedgerLoading(true);
    try {
      const params = { page: ledgerPage, page_size: 20 };
      if (ledgerFilter !== "all") params.type_filter = ledgerFilter;
      if (dateFrom) params.date_from = dateFrom;
      if (dateTo) params.date_to = dateTo;
      const { data } = await api.get(`/supplier-ledger/${supplier.id}`, { params });
      setLedgerData(data);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLedgerLoading(false);
    }
  };

  useEffect(() => {
    if (activeTab === "ledger") {
      fetchLedger();
    }
  }, [activeTab, ledgerPage, ledgerFilter, dateFrom, dateTo]);

  const grandQty = (data?.items || []).reduce((a, b) => a + (b.total_qty || 0), 0);
  const balanceSummary = data?.supplierDetail?.balance_summary || {};
  const balance = balanceSummary.current_balance || 0;
  const isPayable = balance > 0;

  return (
    <div className="fade-up" data-testid="supplier-detail-page">
      <div className="flex items-center gap-3 mb-4">
        <Button variant="ghost" size="sm" onClick={onBack} data-testid="supplier-detail-back">
          <ArrowLeft className="w-4 h-4 mr-2" /> Back
        </Button>
      </div>

      <div className="mb-6">
        <p className="text-xs uppercase tracking-widest text-muted-foreground mb-2">Supplier</p>
        <h1 className="font-heading text-3xl tracking-tight flex items-center gap-2">
          <Building2 className="w-6 h-6 text-[#9E6D5B]" /> {supplier.name}
        </h1>
        <div className="text-sm text-muted-foreground mt-2 flex flex-wrap gap-4">
          {supplier.phone && (
            <span className="flex items-center gap-1">
              <Phone className="w-3 h-3" /> <span className="font-mono-data">{supplier.phone}</span>
            </span>
          )}
          {supplier.email && (
            <span className="flex items-center gap-1">
              <Mail className="w-3 h-3" /> {supplier.email}
            </span>
          )}
          {supplier.gst && (
            <span className="flex items-center gap-1">
              <FileText className="w-3 h-3" /> GST: <span className="font-mono-data">{supplier.gst}</span>
            </span>
          )}
        </div>
        {supplier.address && <div className="text-sm text-muted-foreground mt-1">{supplier.address}</div>}
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <TabsList className="mb-4">
          <TabsTrigger value="overview" data-testid="tab-overview">Overview</TabsTrigger>
          <TabsTrigger value="ledger" data-testid="tab-ledger">Ledger</TabsTrigger>
          <TabsTrigger value="products" data-testid="tab-products">Products</TabsTrigger>
        </TabsList>

        <TabsContent value="overview">
          {/* Balance Summary Card */}
          <Card className="p-5 mb-4">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-semibold flex items-center gap-2">
                <Wallet className="w-5 h-5 text-[#9E6D5B]" /> Balance Summary
              </h3>
              <div className="flex gap-2">
                <Button size="sm" variant="outline" onClick={() => setOpeningBalanceOpen(true)} data-testid="set-opening-balance-btn">
                  Set Opening Balance
                </Button>
                <Button
                  size="sm"
                  style={{ backgroundColor: "#9E6D5B" }}
                  onClick={() => setPaymentOpen(true)}
                  data-testid="add-payment-btn"
                >
                  <CreditCard className="w-4 h-4 mr-1" /> Add Payment
                </Button>
              </div>
            </div>

            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
              <div>
                <div className="text-xs uppercase tracking-wider text-muted-foreground mb-1">Total Purchases</div>
                <div className="font-mono-data text-xl font-semibold">₹{(balanceSummary.total_purchases || 0).toLocaleString()}</div>
              </div>
              <div>
                <div className="text-xs uppercase tracking-wider text-muted-foreground mb-1">Total Payments</div>
                <div className="font-mono-data text-xl font-semibold">₹{(balanceSummary.total_payments || 0).toLocaleString()}</div>
              </div>
              <div>
                <div className="text-xs uppercase tracking-wider text-muted-foreground mb-1">Opening Balance</div>
                <div className="font-mono-data text-xl font-semibold">₹{(balanceSummary.opening_balance || 0).toLocaleString()}</div>
              </div>
              <div>
                <div className="text-xs uppercase tracking-wider text-muted-foreground mb-1">Current Balance</div>
                <div className={`font-mono-data text-xl font-bold ${isPayable ? "text-red-600" : balance < 0 ? "text-green-600" : ""}`}>
                  ₹{Math.abs(balance).toLocaleString()}
                  {balance !== 0 && (
                    <Badge
                      variant="outline"
                      className={`ml-2 text-[10px] ${
                        isPayable ? "border-red-200 bg-red-50 text-red-700" : "border-green-200 bg-green-50 text-green-700"
                      }`}
                    >
                      {isPayable ? "PAYABLE" : "ADVANCE"}
                    </Badge>
                  )}
                </div>
              </div>
            </div>
          </Card>

          {/* Quick Stats */}
          <div className="grid grid-cols-2 gap-4 mb-4">
            <Card className="p-4">
              <div className="text-xs uppercase tracking-widest text-muted-foreground mb-1">Total Units Ordered</div>
              <div className="font-mono-data text-3xl">{grandQty.toLocaleString()}</div>
            </Card>
            <Card className="p-4">
              <div className="text-xs uppercase tracking-widest text-muted-foreground mb-1">Distinct Products</div>
              <div className="font-mono-data text-3xl">{data?.items?.length ?? 0}</div>
            </Card>
          </div>

          {/* Supplier Info Card */}
          {supplier.notes && (
            <Card className="p-4">
              <div className="text-xs uppercase tracking-widest text-muted-foreground mb-2">Notes</div>
              <p className="text-sm">{supplier.notes}</p>
            </Card>
          )}
        </TabsContent>

        <TabsContent value="ledger">
          {/* Filters */}
          <Card className="p-3 mb-4">
            <div className="flex flex-wrap items-center gap-3">
              <div className="flex items-center gap-2">
                <Filter className="w-4 h-4 text-muted-foreground" />
                <Select value={ledgerFilter} onValueChange={(v) => { setLedgerFilter(v); setLedgerPage(1); }}>
                  <SelectTrigger className="w-[140px] h-9">
                    <SelectValue placeholder="Filter type" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Types</SelectItem>
                    <SelectItem value="PURCHASE">Purchases</SelectItem>
                    <SelectItem value="PAYMENT">Payments</SelectItem>
                    <SelectItem value="OPENING_BALANCE">Opening Bal</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="flex items-center gap-2">
                <Calendar className="w-4 h-4 text-muted-foreground" />
                <Input
                  type="date"
                  value={dateFrom}
                  onChange={(e) => { setDateFrom(e.target.value); setLedgerPage(1); }}
                  className="w-[140px] h-9"
                  placeholder="From"
                />
                <span className="text-muted-foreground">to</span>
                <Input
                  type="date"
                  value={dateTo}
                  onChange={(e) => { setDateTo(e.target.value); setLedgerPage(1); }}
                  className="w-[140px] h-9"
                  placeholder="To"
                />
              </div>
              {(dateFrom || dateTo || ledgerFilter !== "all") && (
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => {
                    setDateFrom("");
                    setDateTo("");
                    setLedgerFilter("all");
                    setLedgerPage(1);
                  }}
                >
                  Clear filters
                </Button>
              )}
            </div>
          </Card>

          {/* Balance Summary in Ledger Tab */}
          {ledgerData?.balance && (
            <div className="grid grid-cols-4 gap-3 mb-4">
              <Card className="p-3 text-center">
                <div className="text-xs uppercase text-muted-foreground">Purchases</div>
                <div className="font-mono-data text-lg font-semibold text-blue-600">
                  ₹{(ledgerData.balance.total_purchases || 0).toLocaleString()}
                </div>
              </Card>
              <Card className="p-3 text-center">
                <div className="text-xs uppercase text-muted-foreground">Payments</div>
                <div className="font-mono-data text-lg font-semibold text-green-600">
                  ₹{(ledgerData.balance.total_payments || 0).toLocaleString()}
                </div>
              </Card>
              <Card className="p-3 text-center">
                <div className="text-xs uppercase text-muted-foreground">Opening</div>
                <div className="font-mono-data text-lg font-semibold">
                  ₹{(ledgerData.balance.opening_balance || 0).toLocaleString()}
                </div>
              </Card>
              <Card className={`p-3 text-center ${ledgerData.balance.current_balance > 0 ? "bg-red-50" : ledgerData.balance.current_balance < 0 ? "bg-green-50" : ""}`}>
                <div className="text-xs uppercase text-muted-foreground">Balance</div>
                <div className={`font-mono-data text-lg font-bold ${ledgerData.balance.current_balance > 0 ? "text-red-600" : ledgerData.balance.current_balance < 0 ? "text-green-600" : ""}`}>
                  ₹{Math.abs(ledgerData.balance.current_balance || 0).toLocaleString()}
                </div>
              </Card>
            </div>
          )}

          {/* Ledger Table */}
          <Card className="overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-[hsl(var(--muted))]">
                  <tr className="text-left">
                    <th className="p-3 text-xs uppercase tracking-wider text-muted-foreground font-medium">Date</th>
                    <th className="p-3 text-xs uppercase tracking-wider text-muted-foreground font-medium">Type</th>
                    <th className="p-3 text-xs uppercase tracking-wider text-muted-foreground font-medium">Description</th>
                    <th className="p-3 text-xs uppercase tracking-wider text-muted-foreground font-medium text-right">Debit</th>
                    <th className="p-3 text-xs uppercase tracking-wider text-muted-foreground font-medium text-right">Credit</th>
                    <th className="p-3 text-xs uppercase tracking-wider text-muted-foreground font-medium text-right">Balance</th>
                  </tr>
                </thead>
                <tbody>
                  {ledgerLoading ? (
                    <tr>
                      <td colSpan={6} className="p-10 text-center text-muted-foreground">Loading...</td>
                    </tr>
                  ) : (ledgerData?.entries || []).length === 0 ? (
                    <tr>
                      <td colSpan={6} className="p-10 text-center text-muted-foreground">
                        No ledger entries yet. Purchases and payments will appear here.
                      </td>
                    </tr>
                  ) : (
                    (ledgerData?.entries || []).map((entry) => (
                      <tr key={entry.id} className="border-t border-[hsl(var(--border))] hover:bg-muted/30">
                        <td className="p-3 font-mono-data text-xs">
                          {formatDate(entry.date)}
                        </td>
                        <td className="p-3">
                          <Badge
                            variant="outline"
                            className={`text-[10px] ${
                              entry.type === "PURCHASE"
                                ? "border-blue-200 bg-blue-50 text-blue-700"
                                : entry.type === "PAYMENT"
                                ? "border-green-200 bg-green-50 text-green-700"
                                : "border-amber-200 bg-amber-50 text-amber-700"
                            }`}
                          >
                            {entry.type === "PURCHASE" && <Receipt className="w-3 h-3 mr-1" />}
                            {entry.type === "PAYMENT" && <CreditCard className="w-3 h-3 mr-1" />}
                            {entry.type === "OPENING_BALANCE" && <Clock className="w-3 h-3 mr-1" />}
                            {entry.type}
                          </Badge>
                        </td>
                        <td className="p-3 text-sm max-w-[200px] truncate" title={entry.description}>
                          {entry.description || "—"}
                          {entry.reference_id && (
                            <div className="text-xs text-muted-foreground font-mono-data">
                              Ref: {entry.reference_id.slice(0, 8)}...
                            </div>
                          )}
                        </td>
                        <td className="p-3 text-right font-mono-data">
                          {entry.debit > 0 ? (
                            <span className="text-green-600">₹{entry.debit.toLocaleString()}</span>
                          ) : (
                            "—"
                          )}
                        </td>
                        <td className="p-3 text-right font-mono-data">
                          {entry.credit > 0 ? (
                            <span className="text-red-600">₹{entry.credit.toLocaleString()}</span>
                          ) : (
                            "—"
                          )}
                        </td>
                        <td className={`p-3 text-right font-mono-data font-semibold ${entry.balance > 0 ? "text-red-600" : entry.balance < 0 ? "text-green-600" : ""}`}>
                          ₹{Math.abs(entry.balance).toLocaleString()}
                          {entry.balance !== 0 && (
                            <span className="text-[10px] ml-1">{entry.balance > 0 ? "Dr" : "Cr"}</span>
                          )}
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {ledgerData?.pagination && ledgerData.pagination.total_pages > 1 && (
              <div className="p-3 border-t flex items-center justify-between">
                <div className="text-xs text-muted-foreground">
                  Page {ledgerData.pagination.page} of {ledgerData.pagination.total_pages} ({ledgerData.pagination.total} entries)
                </div>
                <div className="flex gap-2">
                  <Button
                    size="sm"
                    variant="outline"
                    disabled={ledgerPage <= 1}
                    onClick={() => setLedgerPage((p) => p - 1)}
                  >
                    <ChevronLeft className="w-4 h-4" />
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    disabled={ledgerPage >= ledgerData.pagination.total_pages}
                    onClick={() => setLedgerPage((p) => p + 1)}
                  >
                    <ChevronRight className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            )}
          </Card>
        </TabsContent>

        <TabsContent value="products">
          <Card className="overflow-hidden" data-testid="supplier-products-table">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-[hsl(var(--muted))]">
                  <tr className="text-left">
                    <th className="p-3 text-xs uppercase tracking-wider text-muted-foreground font-medium">Product</th>
                    <th className="p-3 text-xs uppercase tracking-wider text-muted-foreground font-medium">Brand / Group</th>
                    <th className="p-3 text-xs uppercase tracking-wider text-muted-foreground font-medium text-right">Total Qty</th>
                    <th className="p-3 text-xs uppercase tracking-wider text-muted-foreground font-medium text-right">Orders</th>
                    <th className="p-3 text-xs uppercase tracking-wider text-muted-foreground font-medium text-right">Last Cost</th>
                    <th className="p-3 text-xs uppercase tracking-wider text-muted-foreground font-medium">Last Ordered</th>
                  </tr>
                </thead>
                <tbody>
                  {(data?.items || []).map((p) => (
                    <tr key={p.product_id} className="data-row border-t border-[hsl(var(--border))]">
                      <td className="p-3 max-w-[320px]">
                        <div className="font-medium truncate" title={p.product}>{p.product}</div>
                        <div className="text-xs font-mono-data text-muted-foreground">{p.auto_barcode}</div>
                      </td>
                      <td className="p-3">
                        <div>{p.brand_name || "—"}</div>
                        <div className="text-xs text-muted-foreground">{p.group_name || "—"}</div>
                      </td>
                      <td className="p-3 text-right font-mono-data text-lg font-semibold text-[#9E6D5B]">
                        {p.total_qty}
                      </td>
                      <td className="p-3 text-right font-mono-data text-muted-foreground">{p.times_ordered}</td>
                      <td className="p-3 text-right font-mono-data">₹{Number(p.last_purchase_cost || 0).toFixed(2)}</td>
                      <td className="p-3 text-xs text-muted-foreground font-mono-data">
                        {p.last_date ? formatDate(p.last_date) : "—"}
                      </td>
                    </tr>
                  ))}
                  {!loading && !(data?.items?.length) && (
                    <tr>
                      <td colSpan={6} className="p-10 text-center text-sm text-muted-foreground">
                        No purchases from this supplier yet.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Add Payment Dialog */}
      <PaymentDialog
        open={paymentOpen}
        onOpenChange={setPaymentOpen}
        supplierId={supplier.id}
        supplierName={supplier.name}
        currentBalance={balance}
        onSuccess={() => {
          fetchLedger();
          // Refresh supplier data
          api.get(`/suppliers/${supplier.id}`).then((res) => {
            setData((prev) => ({ ...prev, supplierDetail: res.data }));
          });
        }}
      />

      {/* Opening Balance Dialog */}
      <OpeningBalanceDialog
        open={openingBalanceOpen}
        onOpenChange={setOpeningBalanceOpen}
        supplierId={supplier.id}
        supplierName={supplier.name}
        currentOpeningBalance={balanceSummary.opening_balance || 0}
        onSuccess={() => {
          fetchLedger();
          api.get(`/suppliers/${supplier.id}`).then((res) => {
            setData((prev) => ({ ...prev, supplierDetail: res.data }));
          });
        }}
      />
    </div>
  );
}

function PaymentDialog({ open, onOpenChange, supplierId, supplierName, currentBalance, onSuccess }) {
  const [amount, setAmount] = useState("");
  const [mode, setMode] = useState("cash");
  const [reference, setReference] = useState("");
  const [description, setDescription] = useState("");
  const [saving, setSaving] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    const amt = parseFloat(amount);
    if (!amt || amt <= 0) {
      toast.error("Please enter a valid amount");
      return;
    }

    setSaving(true);
    try {
      await api.post(`/supplier-ledger/${supplierId}/payment`, {
        type: "PAYMENT",
        amount: amt,
        payment_mode: mode,
        reference_number: reference || null,
        description: description || null,
      });
      toast.success(`Payment of ₹${amt.toLocaleString()} recorded`);
      onOpenChange(false);
      setAmount("");
      setMode("cash");
      setReference("");
      setDescription("");
      onSuccess?.();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="payment-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading tracking-tight flex items-center gap-2">
            <CreditCard className="w-5 h-5 text-[#9E6D5B]" /> Record Payment
          </DialogTitle>
          <DialogDescription>
            Add a payment to <strong>{supplierName}</strong>.
            {currentBalance > 0 && (
              <span className="text-red-600 ml-1">Current payable: ₹{currentBalance.toLocaleString()}</span>
            )}
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <Label className="text-xs uppercase tracking-wider text-muted-foreground">Amount *</Label>
            <div className="relative mt-1">
              <IndianRupee className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
              <Input
                type="number"
                step="0.01"
                min="0.01"
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                className="pl-9 font-mono-data"
                placeholder="0.00"
                required
                data-testid="payment-amount-input"
              />
            </div>
            {currentBalance > 0 && (
              <Button
                type="button"
                variant="link"
                size="sm"
                className="text-xs p-0 h-auto mt-1"
                onClick={() => setAmount(currentBalance.toString())}
              >
                Pay full balance (₹{currentBalance.toLocaleString()})
              </Button>
            )}
          </div>

          <div>
            <Label className="text-xs uppercase tracking-wider text-muted-foreground">Payment Mode</Label>
            <Select value={mode} onValueChange={setMode}>
              <SelectTrigger className="mt-1">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="cash">Cash</SelectItem>
                <SelectItem value="upi">UPI</SelectItem>
                <SelectItem value="bank">Bank Transfer</SelectItem>
                <SelectItem value="cheque">Cheque</SelectItem>
                <SelectItem value="card">Card</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div>
            <Label className="text-xs uppercase tracking-wider text-muted-foreground">Reference Number</Label>
            <Input
              value={reference}
              onChange={(e) => setReference(e.target.value)}
              className="mt-1"
              placeholder="Transaction ID, cheque number, etc."
              data-testid="payment-reference-input"
            />
          </div>

          <div>
            <Label className="text-xs uppercase tracking-wider text-muted-foreground">Description</Label>
            <Input
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              className="mt-1"
              placeholder="Optional notes"
              data-testid="payment-description-input"
            />
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button type="submit" disabled={saving} style={{ backgroundColor: "#9E6D5B" }} data-testid="payment-submit-btn">
              {saving ? "Recording…" : "Record Payment"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function OpeningBalanceDialog({ open, onOpenChange, supplierId, supplierName, currentOpeningBalance, onSuccess }) {
  const [amount, setAmount] = useState(currentOpeningBalance.toString());
  const [description, setDescription] = useState("Opening Balance");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setAmount(currentOpeningBalance.toString());
  }, [currentOpeningBalance, open]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    const amt = parseFloat(amount);
    if (isNaN(amt)) {
      toast.error("Please enter a valid amount");
      return;
    }

    setSaving(true);
    try {
      await api.post(`/supplier-ledger/${supplierId}/opening-balance`, {
        amount: amt,
        description: description || "Opening Balance",
      });
      toast.success(`Opening balance set to ₹${amt.toLocaleString()}`);
      onOpenChange(false);
      onSuccess?.();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="opening-balance-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading tracking-tight">Set Opening Balance</DialogTitle>
          <DialogDescription>
            Set the starting balance for <strong>{supplierName}</strong>.
            <br />
            <span className="text-amber-600">Positive = We owe supplier (payable)</span>
            <br />
            <span className="text-green-600">Negative = Advance paid to supplier</span>
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <Label className="text-xs uppercase tracking-wider text-muted-foreground">Amount</Label>
            <div className="relative mt-1">
              <IndianRupee className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
              <Input
                type="number"
                step="0.01"
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                className="pl-9 font-mono-data"
                placeholder="0.00"
                required
                data-testid="opening-balance-amount-input"
              />
            </div>
          </div>

          <div>
            <Label className="text-xs uppercase tracking-wider text-muted-foreground">Description</Label>
            <Input
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              className="mt-1"
              placeholder="Opening Balance"
              data-testid="opening-balance-description-input"
            />
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button type="submit" disabled={saving} style={{ backgroundColor: "#9E6D5B" }} data-testid="opening-balance-submit-btn">
              {saving ? "Saving…" : "Set Balance"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
